import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DrawerLink } from '../../app/DrawerLink';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Switch } from '../../components/ui/form';
import { Explicit } from '../../components/ui/Explicit';
import { LoadingPanel } from '../../components/ui/Loader';
import { PresenceAvatar } from '../../components/ui/Presence';
import { useDocumentTitle } from '../../lib/title';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { jamLink, type Jam } from './api';
import { InviteFriendsDialog } from './InviteFriendsDialog';
import { JamChat } from './JamChat';
import { useJam } from './useJam';

/**
 * A jam: who is in it, and what is queued.
 *
 * ## The link is the door
 *
 * Whoever opens `/jam/{code}` and is not in it sees who is and a Join
 * button; pressing it puts them in. There is no invitation to accept
 * beyond that - a message with the jam in it is a message with this link
 * in it, dressed up.
 *
 * ## Everybody's queue, everybody's second
 *
 * The queue is shared and so is the clock. "Listen along" starts this
 * person's player where the jam is, and from then on `JamWatcher` keeps it
 * there: a pause, a skip or a scrub by anybody reaches everybody, and the
 * host's player reports its position so nobody drifts. Whoever added a
 * track, or the host, can take it out of the queue.
 */
/**
 * The window, minus the header and the dock - on a wide screen only.
 *
 * The dock is fixed to the bottom and draws over whatever is under it, so
 * a page that asked for the whole window put its own last row - the
 * chat's box - underneath it. The messages page and the console measure
 * themselves the same way.
 *
 * Below `lg` the page scrolls like any other. Two panes each with their
 * own scrollbar inside a phone's height gives both of them a few
 * centimetres and neither of them enough: the queue was three rows tall
 * under a chat nobody had asked to see yet.
 */
const FULL = 'lg:h-[calc(100dvh-var(--spacing-chrome-top)-var(--spacing-dock))] lg:-mb-6 lg:overflow-hidden';

export function JamPage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const me = useSession((state) => state.user);
  const current = usePlayer((state) => state.track);
  const source = usePlayer((state) => state.source);
  const isPlaying = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);
  const { data, isPending, isError, join, leave, remove, playFrom, listen, settings, move } = useJam(code);
  /*
   * What is being dragged, and what it is hovering over. Held here rather
   * than in each row so a row can light its own edge without every other
   * row re-rendering on every pointer move.
   */
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [showPlayed, setShowPlayed] = useState(false);

  useDocumentTitle(data ? `Jam with ${data.members.find((m) => m.is_host)?.display_name ?? 'friends'}` : 'Jam');

  if (isPending) return <LoadingPanel label="Opening the jam" className="mt-16" />;

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-[760px] px-6 py-16 text-soft" data-jam-missing>
        That jam is not here. The link may be wrong, or it may have ended.
      </div>
    );
  }

  const host = data.members.find((m) => m.is_host);
  const ended = data.status === 'ended';
  const listening = source === 'jam' && current !== null;
  // What the jam's clock points at, whether or not this player is on it.
  const nowItem = data.playback.item_id;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(jamLink(code));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // A clipboard that refuses is a clipboard the address bar can stand in for.
    }
  };

  return (
    /*
      The window, not a document in it.

      A jam is a room somebody sits in for an hour with the queue on one
      side and the talk on the other, and both want to be where they were
      left. So the page is the viewport's height and never scrolls; each
      column scrolls inside itself, the way the mastering desk and the
      console do. The dock stays, unlike the desk's, because the whole
      point of being here is listening.
    */
    <div className={`${FULL} flex min-h-0 flex-col lg:flex-row`} data-jam-page={code}>
      {/*
        The jam on the left and the talk about it on the right. The left
        gets the width because a queue is a list of long titles; the right
        is a column of short lines and takes a fixed one.
      */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 px-4 py-6 sm:px-6 lg:overflow-y-auto">
      <header className="flex flex-col gap-4">
        {/*
          The title and the room on one line, the way a room reads: what
          this is, who is in it, and - as a live dot rather than a
          sentence - whether it is playing.
        */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="meta-caps flex items-center gap-2 text-faint">
              {!ended && (
                <span aria-hidden className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-fresh opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-fresh" />
                </span>
              )}
              {ended ? 'This jam has ended' : 'Jam'}
            </span>
            <h1 className="truncate text-3xl font-bold tracking-tight text-ink">
              {host ? `${host.display_name}'s jam` : 'A jam'}
            </h1>
          </div>

          {/* Who is here, as faces: overlapped, the way a room of people is. */}
          <ul className="flex items-center" data-jam-members>
            {data.members.map((member) => (
              <li key={member.id} data-jam-member={member.username} className="-ml-2 first:ml-0">
                <Link
                  to={`/${member.username}`}
                  title={`${member.display_name}${member.is_host ? ' · host' : ''}`}
                  className="block rounded-full ring-2 ring-app transition-transform hover:z-10 hover:scale-110"
                >
                  <PresenceAvatar src={member.avatar} name={member.display_name} size={32} />
                </Link>
              </li>
            ))}
            <li className="ml-3 text-[13px] text-faint">
              {data.members.length === 1 ? 'just you' : `${data.members.length} in the room`}
            </li>
          </ul>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!ended && !data.is_member && me && (
            <Button data-jam-join loading={join.isPending} onClick={() => join.mutate()}>
              Join
            </Button>
          )}

          {!ended && data.is_member && (
            <>
              <Button
                disabled={data.queue.length === 0 || listening}
                data-jam-play={listening ? 'listening' : 'idle'}
                onClick={() => void listen()}
              >
                {listening ? 'Listening along' : nowItem !== null ? 'Listen along' : 'Start the music'}
              </Button>
              <Button variant="secondary" onClick={() => setInviting(true)} data-jam-invite-friends>
                Invite friends
              </Button>
              <Button variant="secondary" onClick={() => void copy()} data-jam-copy>
                {copied ? 'Link copied' : 'Copy the link'}
              </Button>
              <Button
                variant="ghost"
                data-jam-leave
                loading={leave.isPending}
                onClick={() => leave.mutate(undefined, { onSuccess: () => navigate('/') })}
              >
                {data.is_host ? 'End the jam' : 'Leave'}
              </Button>
            </>
          )}
        </div>

        {/*
          The host's two switches, as switches: a thing that is on or off
          right now, with no Save anywhere near it. Side by side on a wide
          screen, because they are the same kind of decision about the
          same room.
        */}
        {!ended && data.is_member && data.is_host && (
          <div className="grid gap-px overflow-hidden rounded-[var(--radius-card)] border border-line bg-line sm:grid-cols-2">
            <Setting
              on={data.guests_control}
              busy={settings.isPending}
              title="Guests control playback"
              note={data.guests_control ? 'Anybody can pause, skip or scrub' : 'Only you can; guests still add to the queue'}
              test={{ 'data-jam-guests-control': data.guests_control ? 'on' : 'off' }}
              onChange={(next) => settings.mutate({ guests_control: next })}
            />
            {/*
              The room's repeat, not a listener's. A jam seeded with an
              album is meant to end when the album does; one people are
              sitting in all evening is not, and this is the difference.
            */}
            <Setting
              on={data.repeats}
              busy={settings.isPending}
              title="Start again at the end"
              note={data.repeats ? 'The queue loops' : 'The queue stops when it runs out'}
              test={{ 'data-jam-repeat': data.repeats ? 'on' : 'off' }}
              onChange={(next) => settings.mutate({ repeats: next })}
            />
          </div>
        )}

        {!ended && data.is_member && (
          <p className="text-[13px] leading-relaxed text-faint" data-jam-rules>
            {data.is_host
              ? 'Everyone hears the same thing at the same time. Whatever you play, the jam follows. Guests add to the queue by tapping any track.'
              : data.guests_control
                ? 'Everyone hears the same thing at the same time. Tap any track to add it to the queue, and pause, skip or scrub for everybody. To play your own music again, leave the jam.'
                : 'Everyone hears the same thing at the same time. Tap any track to add it to the queue. The host controls playback in this jam. To play your own music again, leave the jam.'}
          </p>
        )}
      </header>

      {!ended && data.queue.length === 0 && (
        <Card className="p-6 text-sm text-soft" data-jam-empty>
          {data.is_host
            ? 'Play anything and the jam follows you: a playlist, an album, a track. Whatever you press play on becomes what everybody hears, and the plus beside any track adds to it.'
            : 'Nothing playing yet. When the host plays something, it lands here; the plus beside any track adds to it.'}
        </Card>
      )}

      {/*
        Three sections, the way a queue reads: what is playing, what people
        added (it plays next), then the rest of what the jam is playing
        through. What has already played is folded away at the top.
      */}
      {data.queue.length > 0 && (
        <div className="flex flex-col gap-5" data-jam-queue>
          {sections(data.queue, nowItem).map((section) =>
            section.items.length === 0 ? null : (
              <section key={section.key} data-jam-section={section.key}>
                {/*
                  What the room has already heard is folded away. An evening
                  of a jam is forty rows of history above the one thing
                  anybody opened the page to see, which is what is playing.
                */}
                {section.key === 'played' ? (
                  <button
                    type="button"
                    onClick={() => setShowPlayed((was) => !was)}
                    data-jam-played-toggle={showPlayed ? 'open' : 'shut'}
                    className="meta-caps mb-2 flex items-center gap-2 text-faint transition-colors hover:text-ink"
                  >
                    {section.title}
                    <span className="text-ghost">{section.items.length}</span>
                    <span aria-hidden className="text-ghost">{showPlayed ? '▾' : '▸'}</span>
                  </button>
                ) : (
                  <h2 className="meta-caps mb-2 text-faint">
                    {section.key === 'context' && data.context?.label
                      ? `Next from ${data.context.label}`
                      : section.title}
                    <span className="ml-2 text-ghost">{section.items.length}</span>
                  </h2>
                )}
                <ol className={`flex flex-col gap-1 ${section.key === 'played' && !showPlayed ? 'hidden' : ''}`}>
                  {section.items.map(({ item, index }) => renderItem(item, index))}
                </ol>
              </section>
            ),
          )}
        </div>
      )}
      </div>

      {/*
        The chat, the height of the window and scrolling inside itself.
        Bordered rather than carded here: it is a wall of the room, not a
        panel floating in it.
      */}
      {!ended && data.is_member && (
        <div className="flex max-h-[70vh] min-h-[320px] w-full shrink-0 border-t border-line lg:max-h-none lg:min-h-0 lg:w-[380px] lg:border-t-0 lg:border-l">
          <JamChat code={code} jamId={data.id} canSay />
        </div>
      )}

      {inviting && <InviteFriendsDialog jamId={data.id} onClose={() => setInviting(false)} />}
    </div>
  );

  function renderItem(item: Jam['queue'][number], index: number) {
            const jam = data!;
            const track = item.track;
            // Lit by the jam's clock, so a member who has not pressed Listen
            // still sees what everybody else is hearing.
            const playing = item.item_id === nowItem;
            // Playing here, not just playing somewhere: a pause is only
            // offered to somebody who can hear what they are pausing.
            const pausable = playing && listening && isPlaying;
            const canRemove = jam.is_member && (jam.is_host || item.added_by?.id === me?.id);
            const played = !playing && index < jam.queue.findIndex((i) => i.item_id === nowItem);
            /*
             * Only what is still to play can be dragged, and only by
             * somebody who could skip to it anyway - the same switch. What
             * has played is a record of what the room heard.
             */
            const canDrag = jam.is_member && !ended && !played && !playing && (jam.is_host || jam.guests_control);

            return (
              <li
                key={item.item_id}
                data-jam-item={item.item_id}
                data-jam-item-kind={item.kind}
                draggable={canDrag}
                onDragStart={(event) => {
                  setDragging(item.item_id);
                  event.dataTransfer.effectAllowed = 'move';
                  // Firefox drags nothing without payload, whatever the payload is.
                  event.dataTransfer.setData('text/plain', String(item.item_id));
                }}
                onDragEnd={() => {
                  setDragging(null);
                  setOver(null);
                }}
                onDragOver={(event) => {
                  if (!canDrag || dragging === null || dragging === item.item_id) return;

                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setOver(item.item_id);
                }}
                onDragLeave={() => setOver((held) => (held === item.item_id ? null : held))}
                onDrop={(event) => {
                  event.preventDefault();

                  const held = dragging;

                  setDragging(null);
                  setOver(null);

                  if (held === null || held === item.item_id || !canDrag) return;

                  move.mutate({ itemId: held, before: item.item_id });
                }}
                data-jam-drop={over === item.item_id ? 'over' : undefined}
                className={`flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-2 transition-colors hover:bg-raised ${
                  playing ? 'bg-raised' : played ? 'opacity-50' : ''
                } ${dragging === item.item_id ? 'opacity-40' : ''} ${
                  over === item.item_id ? 'border-t-2 border-accent' : 'border-t-2 border-transparent'
                } ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                <span className="w-5 shrink-0 text-right font-mono text-[11px] text-ghost">{index + 1}</span>

                {/*
                  The row that is playing offers a pause, like every other
                  play button on the site. It pauses the jam rather than
                  this player: everybody is listening to one clock, and a
                  button that silenced you alone while the room played on
                  would be a different feature wearing this one's clothes.
                  Only while actually listening along - for somebody
                  watching the queue without the sound, the same row is
                  still the way in.
                */}
                <button
                  type="button"
                  disabled={!jam.is_member || ended}
                  onClick={() => (pausable ? toggle() : playFrom(jam, item.item_id))}
                  aria-label={pausable ? `Pause ${track.title}` : `Play ${track.title}`}
                  data-jam-play-item={item.item_id}
                  data-jam-item-state={playing ? (pausable ? 'pausable' : 'playing') : 'idle'}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] transition-colors ${
                    playing ? 'bg-accent text-accent-ink' : 'bg-line text-soft hover:bg-accent hover:text-accent-ink'
                  } disabled:opacity-40`}
                >
                  <svg viewBox="0 0 16 16" className={`h-3.5 w-3.5 ${pausable ? '' : 'translate-x-px'}`} fill="currentColor" aria-hidden="true">
                    {pausable ? (
                      <path d="M4.5 3h2.6v10H4.5zM8.9 3h2.6v10H8.9z" />
                    ) : (
                      <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
                    )}
                  </svg>
                </button>

                {track.artwork_small && (
                  <img src={track.artwork_small} alt="" className="h-9 w-9 shrink-0 rounded-[var(--radius-control)] object-cover" />
                )}

                <DrawerLink to={`/tracks/${track.id}`} className="min-w-0 flex-1 leading-tight">
                  <span className="flex items-center gap-1.5 truncate text-sm font-medium text-ink">
                    {track.explicit && <Explicit />}
                    <span className="truncate">{track.title}</span>
                  </span>
                  <span className="meta block truncate text-faint">
                    {track.owner?.display_name ?? ''}
                    {item.added_by && <span className="text-ghost"> · added by @{item.added_by.username}</span>}
                  </span>
                </DrawerLink>

                {canRemove && !ended && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(item.item_id)}
                    disabled={remove.isPending}
                    aria-label={`Remove ${track.title} from the jam`}
                    data-jam-remove={item.item_id}
                    className="shrink-0 rounded px-1.5 py-1 font-mono text-[11px] text-ghost transition-colors hover:text-hot-text"
                  >
                    ×
                  </button>
                )}
              </li>
            );
  }
}

/** One of the host's switches: what it does, what it is doing now, and the switch. */
function Setting({
  on,
  busy,
  title,
  note,
  test,
  onChange,
}: {
  on: boolean;
  busy: boolean;
  title: string;
  note: string;
  test: Record<string, string>;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 bg-surface px-4 py-3" {...test}>
      <span className="flex min-w-0 flex-col">
        <span className="text-[13px] font-medium text-ink">{title}</span>
        <span className="meta text-faint">{note}</span>
      </span>
      <Switch on={on} onChange={onChange} label={title} disabled={busy} size="sm" />
    </div>
  );
}

/**
 * The queue cut at the clock: what played, what is playing, what people
 * added after it, and the context after that. Added items sit before the
 * context by the server's ordering rule, so this is a walk, not a sort.
 */
function sections(queue: Jam['queue'], nowItem: number | null) {
  const at = queue.findIndex((i) => i.item_id === nowItem);
  const played: { item: Jam['queue'][number]; index: number }[] = [];
  const now: { item: Jam['queue'][number]; index: number }[] = [];
  const added: { item: Jam['queue'][number]; index: number }[] = [];
  const context: { item: Jam['queue'][number]; index: number }[] = [];

  queue.forEach((item, index) => {
    const entry = { item, index };

    if (at !== -1 && index < at) played.push(entry);
    else if (index === at) now.push(entry);
    else if (item.kind === 'added') added.push(entry);
    else context.push(entry);
  });

  return [
    { key: 'played', title: 'Played', items: played },
    { key: 'now', title: 'Now playing', items: now },
    { key: 'added', title: 'Next in the jam', items: added },
    { key: 'context', title: 'Next', items: context },
  ];
}
